import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import React, { useCallback, useMemo } from 'react'
import { SectionItem, SectionWrapped } from '../TopBarDropdown/TopBarDropdownSectionItem.component'
import { useLocation, useNavigate } from 'react-router'

import { GRAY_800 } from 'constants/styling/theme'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import { MUIDivider } from 'components/common/MUIDivider'
import { Path } from 'constants/router'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import Stack from '@mui/material/Stack'
import { TabButton } from 'components/common/TabButton'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/** Enum representing the navigation paths for the TopBar component. */
enum NavigationPaths {
  PROPERTIES_DASHBOARD = 'PROPERTIES_DASHBOARD',
  ORDERS = 'ORDERS',
}

/**
 * @interface Props for TopBarNavigation component
 */
export interface Props {
  /** Whether the navigation is displayed on a mobile device */
  isMobile?: boolean
  /** Callback to close the parent mobile menu */
  onCloseMenu?: (e: React.MouseEvent<Element, MouseEvent>) => void
}

/**
 * @component
 * TopBarNavigation renders a navigation bar with tabs.
 *
 * @example
 *  <TopBarNavigation />
 */
export const TopBarNavigation: React.FC<Props> = ({ isMobile = false, onCloseMenu }) => {
  const { roles } = useAuth0()

  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation(['topbar'])
  const { currentUserWorkspace } = useUserData()

  const activeTab = useMemo(() => {
    // Set the active tab based on the current location
    if (location.pathname === Path.ORDERS || location.pathname.startsWith('/gallery')) return NavigationPaths.ORDERS

    // Here we can add more conditions to set the active tab based on the current location
    if (location.pathname === Path.INDEX || location.pathname.startsWith('/property') || location.pathname.startsWith('/properties')) {
      return NavigationPaths.PROPERTIES_DASHBOARD
    }

    return null
  }, [location.pathname])

  const tabs = useMemo(() => {
    return Object.values(NavigationPaths).map((path) => ({
      label: path,
      path,
    }))
  }, [])

  const tabIcon = useCallback((path: NavigationPaths) => {
    switch (path) {
      case NavigationPaths.PROPERTIES_DASHBOARD:
        return <HomeOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_800 }} />
      case NavigationPaths.ORDERS:
        return <ShoppingCartOutlinedIcon sx={{ fontSize: '1.8rem', color: GRAY_800 }} />
      default:
        return null
    }
  }, [])

  /** Handles the tab click event by redirecting to the selected tab. */
  const handleTabClick = useCallback(
    (path: NavigationPaths) => {
      if (path === NavigationPaths.ORDERS && location.pathname !== Path.ORDERS) {
        logAnalyticsEvent(AnalyticsEvent.OPEN_ORDERS_DASHBOARD, {})
        navigate(Path.ORDERS)
      }

      if (path === NavigationPaths.PROPERTIES_DASHBOARD && location.pathname !== Path.INDEX) {
        logAnalyticsEvent(AnalyticsEvent.OPEN_PROPERTY_DASHBOARD, {})
        navigate(Path.INDEX)
      }
    },
    [location.pathname, navigate]
  )

  if (!roles.isClient || !currentUserWorkspace) return <></>

  return (
    <>
      {isMobile ? (
        <Stack width="100%">
          <SectionWrapped>
            {tabs.map(({ path, label }) => (
              <SectionItem
                key={path}
                isMobile
                label={t(`navigation_tab.${label}`)}
                icon={tabIcon(path)}
                isActive={activeTab === path}
                onClick={(e) => {
                  handleTabClick(path)
                  onCloseMenu?.(e)
                }}
              />
            ))}
          </SectionWrapped>

          <MUIDivider margin={16} />
        </Stack>
      ) : (
        <Stack flex={1} justifyContent="center" direction="row" gap={1.2}>
          {tabs.map(({ path, label }) => (
            <TabButton key={path} label={t(`navigation_tab.${label}`)} icon={tabIcon(path)} isActive={activeTab === path} onClick={() => handleTabClick(path)} />
          ))}
        </Stack>
      )}
    </>
  )
}
